import React, { useState, useEffect } from "react";
import axios from "axios";
import { message, Alert } from "antd";
import {
  InputItem,
  InputNumberItem,
  ModalItem,
  SelectInsumosItem,
} from "../../../../../Components/Items";
import { ANCHO_DEFAULT, ALTO_DEFAULT } from "../../MapaHotel/Components/nodosHabitaciones";

const MAXIMO_POR_LOTE = 60;
const SEPARACION = 20;

const valoresIniciales = {
  numeroInicial: "101",
  cantidad: 1,
  porFila: 8,
  insumoId: undefined,
  capacidad: undefined,
};

//Genera la serie de números a partir del primero: conserva el prefijo no numérico y el relleno de
//ceros, así que "101" da 101,102,103 y "A-01" da A-01,A-02,A-03. Si no hay dígitos al final se
//agrega un consecutivo con guion, que es mejor que crear varias habitaciones con el mismo número
//(el backend las rechazaría a la segunda).
export const generarNumeros = (numeroInicial, cantidad) => {
  const texto = String(numeroInicial || "").trim();
  const coincidencia = texto.match(/^(.*?)(\d+)$/);

  if (!coincidencia) {
    return Array.from({ length: cantidad }, (_, indice) =>
      cantidad === 1 ? texto : `${texto}-${indice + 1}`
    );
  }

  const prefijo = coincidencia[1];
  const digitos = coincidencia[2];
  const inicio = parseInt(digitos, 10);

  return Array.from({ length: cantidad }, (_, indice) =>
    prefijo + String(inicio + indice).padStart(digitos.length, "0")
  );
};

//Alta de habitaciones en lote. En un hotel los cuartos de un piso son casi siempre iguales y
//consecutivos, así que capturarlos de uno en uno sería el trabajo más aburrido del módulo: aquí se
//dan de alta todos de un golpe, ya acomodados en cuadrícula, y luego se arrastran a su lugar real.
const ModalAgregarHabitaciones = ({
  visible,
  onClose,
  empresaId,
  proyectoId,
  nivelHotelId,
  posicionInicial,
  headersToken,
  onCreadas,
}) => {
  const [valores, setValores] = useState(valoresIniciales);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (visible) setValores(valoresIniciales);
  }, [visible]);

  const actualizar = (cambios) => setValores((prev) => ({ ...prev, ...cambios }));

  const crear = async () => {
    const cantidad = Number(valores.cantidad) || 0;
    if (!String(valores.numeroInicial || "").trim()) {
      message.warning("Captura el número de la primera habitación");
      return;
    }
    if (cantidad < 1) {
      message.warning("La cantidad debe ser al menos 1");
      return;
    }

    const numeros = generarNumeros(valores.numeroInicial, cantidad);
    const porFila = Math.max(1, Number(valores.porFila) || 8);
    const baseX = posicionInicial?.x ?? 40;
    const baseY = posicionInicial?.y ?? 40;

    setGuardando(true);

    // Secuencial y no en paralelo: el alta valida que el número no esté repetido en el hotel, y el
    // backend serializa por proyectoId con un lock de Redis - mandarlas todas de golpe solo haría
    // que se pelearan por el candado.
    const creadas = [];
    const fallidas = [];
    for (let indice = 0; indice < numeros.length; indice += 1) {
      const numero = numeros[indice];
      const x = baseX + (indice % porFila) * (ANCHO_DEFAULT + SEPARACION);
      const y = baseY + Math.floor(indice / porFila) * (ALTO_DEFAULT + SEPARACION);
      try {
        const res = await axios.post(
          "hoteleria/agregarHabitacion",
          {
            empresaId,
            proyectoId,
            nivelHotelId,
            insumoId: valores.insumoId,
            numero,
            capacidad: valores.capacidad,
            x,
            y,
            ancho: ANCHO_DEFAULT,
            alto: ALTO_DEFAULT,
          },
          { headers: headersToken }
        );
        if (res.data.success === 2) {
          creadas.push({
            _id: res.data.habitacionId,
            empresaId,
            proyectoId,
            nivelHotelId,
            insumoId: valores.insumoId,
            numero,
            capacidad: valores.capacidad,
            x,
            y,
            ancho: ANCHO_DEFAULT,
            alto: ALTO_DEFAULT,
            activa: true,
          });
        } else {
          fallidas.push(`${numero}: ${res.data.error || res.data.message || "error"}`);
        }
      } catch (error) {
        console.error("Error al crear la habitación:", error);
        fallidas.push(`${numero}: error de red`);
      }
    }

    setGuardando(false);

    if (creadas.length > 0) {
      message.success(`${creadas.length} habitación(es) creada(s)`);
      onCreadas(creadas);
    }
    // Se reportan una por una en vez de un "hubo errores" genérico: casi siempre es que ese número ya
    // existe, y saber cuál es la diferencia entre corregirlo en un segundo o adivinar.
    if (fallidas.length > 0) {
      message.error(`No se crearon ${fallidas.length}: ${fallidas.slice(0, 5).join(" | ")}`, 8);
    }
    if (fallidas.length === 0) onClose();
  };

  const cantidad = Number(valores.cantidad) || 0;
  const numerosPrevia = cantidad > 0 ? generarNumeros(valores.numeroInicial, Math.min(cantidad, 6)) : [];

  return (
    <ModalItem
      title="Agregar habitaciones"
      visible={visible}
      onCancel={onClose}
      onOk={crear}
      width={560}
      footer
      spinning={guardando}
      confirmLoading={guardando}
    >
      <p style={{ color: "#8a93a6", fontSize: 12 }}>
        Se dan de alta acomodadas en cuadrícula; después las arrastras a su lugar en el plano.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 4, color: "#6b7280" }}>Primer número</div>
          <InputItem
            value={valores.numeroInicial}
            placeholder="101"
            onChange={(e) => actualizar({ numeroInicial: e.target.value })}
          />
        </div>
        <div style={{ width: 110 }}>
          <div style={{ marginBottom: 4, color: "#6b7280" }}>Cantidad</div>
          <InputNumberItem
            style={{ width: "100%" }}
            min={1}
            max={MAXIMO_POR_LOTE}
            value={valores.cantidad}
            onChange={(valor) => actualizar({ cantidad: valor })}
          />
        </div>
        <div style={{ width: 110 }}>
          <div style={{ marginBottom: 4, color: "#6b7280" }}>Por fila</div>
          <InputNumberItem
            style={{ width: "100%" }}
            min={1}
            max={20}
            value={valores.porFila}
            onChange={(valor) => actualizar({ porFila: valor })}
          />
        </div>
      </div>

      {numerosPrevia.length > 0 && (
        <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 12 }}>
          Se crearán: <strong>{numerosPrevia.join(", ")}</strong>
          {cantidad > numerosPrevia.length ? `, ... (${cantidad} en total)` : ""}
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Insumo de la tarifa</div>
        <SelectInsumosItem
          empresaId={empresaId}
          headersToken={headersToken}
          value={valores.insumoId}
          onChange={(valor) => actualizar({ insumoId: valor })}
          width="100%"
          placeholder="Buscar el insumo que se cobra por estas habitaciones..."
          allowClear
        />
        <div style={{ color: "#8a93a6", fontSize: 12, marginTop: 4 }}>
          Es el insumo que se vende por noche (por ejemplo "Habitación Doble"). Lo comparten todas las
          habitaciones del mismo tipo, así que la tarifa se cambia en un solo lugar. Sin insumo la
          habitación se pinta gris y no se puede vender.
        </div>
      </div>

      <div style={{ width: 160 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Capacidad (opcional)</div>
        <InputNumberItem
          style={{ width: "100%" }}
          min={1}
          value={valores.capacidad}
          onChange={(valor) => actualizar({ capacidad: valor })}
        />
      </div>

      {cantidad >= MAXIMO_POR_LOTE && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message={`El máximo por lote es ${MAXIMO_POR_LOTE}. Si el piso tiene más, hazlo en dos tandas.`}
        />
      )}
    </ModalItem>
  );
};

export default ModalAgregarHabitaciones;
